///
/// EarthBrowser
///
/// Created by Matt Giger on 4/10/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type Stovokor } from "@stovokor/Stovokor"
import { type FrameTask } from "@stovokor/tasks/FrameTask"
import { type TimerTask } from "@stovokor/tasks/TimerTask"

export interface TaskManagerProtocol {
	addFrameTask(task: FrameTask): number
	removeFrameTask(taskId: number): void
	addTimerTask(task: TimerTask): number
	removeTimerTask(timerId: number): void
}

export class TaskManager implements TaskManagerProtocol {
	// Binary min-heap on nextActivation: due timers are popped off the top
	// and re-armed ones pushed back, instead of re-sorting the whole array
	// every frame something fires
	private timers: TimerTask[] = []
	private timerCount: number = 1
	private frameTasks: FrameTask[] = []
	private taskCount: number = 1
	private engine: Stovokor

	constructor(engine: Stovokor) {
		this.engine = engine
	}

	public addFrameTask(task: FrameTask): number {
		task.id = this.taskCount++
		this.frameTasks.push(task)
		task.start(this.engine)
		return task.id
	}

	public removeFrameTask(taskId: number): void {
		this.frameTasks = this.frameTasks.filter(task => task.id != taskId)
	}

	public addTimerTask(task: TimerTask): number {
		const timerId = this.timerCount++
		task.id = timerId
		task.nextActivation = performance.now() * 0.001 + task.delay
		this.heapPush(task)
		task.start?.(this.engine)
		return timerId
	}

	public removeTimerTask(timerId: number): void {
		const index = this.timers.findIndex(timer => timer.id === timerId)
		if(index >= 0) {
			this.heapRemoveAt(index)
		}
	}

	/**
	 * Run the frame tasks and any timers that have come due. Returns true
	 * when a timer fired, since that's a discrete event the scene may need
	 * a redraw for
	 */
	public frame(frameId: number): boolean {
		const frameTasks = this.frameTasks
		for(let i = 0; i < frameTasks.length; i++) {
			frameTasks[i].action(frameId)
		}

		const now = performance.now() * 0.001
		if(this.timers.length === 0 || this.timers[0].nextActivation > now) {
			return false
		}

		// Pop everything due before running any of it, so a timer that
		// re-arms itself for a time still <= now fires once per frame
		const due: TimerTask[] = []
		while(this.timers.length > 0 && this.timers[0].nextActivation <= now) {
			due.push(this.heapPop())
		}
		for(const timer of due) {
			timer.action(now)
		}
		for(const timer of due) {
			if(!timer.dead) {
				this.heapPush(timer)
			}
		}
		return true
	}

	private heapPush(task: TimerTask): void {
		const heap = this.timers
		heap.push(task)
		let i = heap.length - 1
		while(i > 0) {
			const parent = (i - 1) >> 1
			if(heap[parent].nextActivation <= heap[i].nextActivation) {
				break
			}
			[heap[parent], heap[i]] = [heap[i], heap[parent]]
			i = parent
		}
	}

	private heapPop(): TimerTask {
		const heap = this.timers
		const top = heap[0]
		const last = heap.pop() as TimerTask
		if(heap.length > 0) {
			heap[0] = last
			this.siftDown(0)
		}
		return top
	}

	private heapRemoveAt(index: number): void {
		const heap = this.timers
		const last = heap.pop() as TimerTask
		if(index < heap.length) {
			heap[index] = last
			this.siftDown(index)
			// The moved element may also need to rise if it is smaller than
			// its new parent
			let i = index
			while(i > 0) {
				const parent = (i - 1) >> 1
				if(heap[parent].nextActivation <= heap[i].nextActivation) {
					break
				}
				[heap[parent], heap[i]] = [heap[i], heap[parent]]
				i = parent
			}
		}
	}

	private siftDown(index: number): void {
		const heap = this.timers
		const length = heap.length
		let i = index
		for(;;) {
			const left = 2 * i + 1
			const right = left + 1
			let smallest = i
			if(left < length && heap[left].nextActivation < heap[smallest].nextActivation) {
				smallest = left
			}
			if(right < length && heap[right].nextActivation < heap[smallest].nextActivation) {
				smallest = right
			}
			if(smallest === i) {
				break
			}
			[heap[smallest], heap[i]] = [heap[i], heap[smallest]]
			i = smallest
		}
	}
}
